import {showError, showSuccess} from '@phonectas/unifon-ui-kit';
import {MrcEntryCreateRequest, MrcItem, MrcListRequest} from 'common/types/store/mrcTypes';
import {MrcModel, MrcModelList, MrcState} from 'containers/MRC/types';
import {getI18n} from 'react-i18next';
import {
	createMrcEntry,
	getBrregOrg,
	getMrcEntryById,
	getMrcList,
	getMrcTemplatesList,
} from 'services/mrcApi';


type SetStateFunction = (updater: (state: MrcState) => Partial<MrcState>) => void;

export function upsertMrcList(list: MrcModelList, item: MrcModel): MrcModelList {
	const idx = list.findIndex((i) => i.mobile === item.mobile);
	if (idx > -1) {
		const updated = [...list];
		updated[idx] = item;
		return updated;
	}
	return [...list, item];
}

class MrcStoreModel {
	private set: SetStateFunction;
	public defaultState: MrcState;

	constructor(set: SetStateFunction) {
		this.set = set;

		// Define default state
		this.defaultState = {
			isLoading: false,
			orgIsLoading: false,
			list: null,
			queryResault: null,
			activeRow: null,
			mrcTemplateList: null,
			activeBrregOrg: null,
		};
	}

	updateState(updater: (state: MrcState) => Partial<MrcState>): void {
		this.set((state) => ({
			...state,
			...updater(state),
		}));
	}

	resetState(): void {
		this.set(() => ({...this.defaultState}));
	}

	resetQueryResult(): void {
		this.set(() => ({queryResault: null}));
	}

	resetActiveBrregOrg(): void {
		this.set(() => ({activeBrregOrg: null}));
	}

	setActiveMrcRow(row: MrcItem | null): void {
		this.set(() => ({activeRow: row}));
	}

	resetMrcActiveRow(): void {
		this.set(() => ({activeRow: null}));
	}

	async fetchMrcTemplates(): Promise<void> {
		this.updateState(() => ({isLoading: true}));
		try {
			const res = await getMrcTemplatesList();
			this.updateState(() => ({
				mrcTemplateList: res,
			}));
		} catch (err) {
			console.error('Error fetching MRC templates:', err);
		} finally {
			this.updateState(() => ({isLoading: false}));
		}
	}

	async fetchMrcEntry(id: number): Promise<void> {
		this.updateState(() => ({isLoading: true}));
		try {
			const res = await getMrcEntryById(id);
			this.updateState(() => ({
				activeRow: res,
				isLoading: false,
			}));
		} catch (err) {
			console.error('Error fetching MRC entry:', err);
		} finally {
			this.updateState(() => ({isLoading: false}));
		}
	}

	async fetchMrcList(params: MrcListRequest): Promise<void> {
		const {
			limit = 50,
			offset = 0,
			search,
			sort_mode = 'createdDesc',
			...rest
		} = params || {};

		// Only include search if it's a non-empty string
		const finalParams: MrcListRequest = {
			limit,
			offset,
			sort_mode,
			...rest,
			...(search && search.trim() !== '' ? {search: search.trim()} : {})
		};

		this.updateState(() => ({isLoading: true}));
		try {
			const res = await getMrcList(finalParams);
			if (search && search.trim() !== '') {
				this.updateState(() => ({
					queryResault: res,
					isLoading: false,
				}));
			} else {
				this.updateState(() => ({
					list: res,
					isLoading: false,
				}));
			}
		} catch (err) {
			console.error('Error fetching MRC list:', err);
		} finally {
			this.updateState(() => ({isLoading: false}));
		}
	}

	// brreg get organisasjon by orgnr
	async fetchBrregOrg(orgnr: string): Promise<void> {
		this.updateState(() => ({orgIsLoading: true}));
		try {
			const res = await getBrregOrg(orgnr);
			this.updateState(() => ({
				activeBrregOrg: res,
				orgIsLoading: false,
			}));
		} catch (err) {
			console.error('Error fetching Brreg organisasjon:', err);
			this.updateState(() => ({
				activeBrregOrg: {
					respons_klasse: 'ERROR',
				}
			}));
		} finally {
			this.updateState(() => ({orgIsLoading: false}));
		}
	}

	async creatMrc(
		data: MrcEntryCreateRequest,
		onSuccess?: () => void,
	): Promise<void> {
		this.updateState(() => ({isLoading: true}));
		try {
			const res = await createMrcEntry(data);

			showSuccess({
				message: getI18n().t('MRC entry created successfully by {{created_by_name}}', {
					created_by_name: res.created_by_name,
					returnObjects: false
				}) as string,
			});
            
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			console.error('Error creating MRC entry:', err);
			showError({
				message: getI18n().t('Failed to create MRC entry', {returnObjects: false}) as string,
			});
		} finally {
			this.updateState(() => ({isLoading: false}));
		}
	}
}

export default MrcStoreModel;